import React, { useEffect, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { addMonths, isValid } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useAuth } from '../contexts/AuthContext';
import { useSubmitLock } from '../hooks/useSubmitLock';
import { financeService } from '../services/financeService';

const formatCurrency = (value) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

const toCents = (value) => Math.round(parseFloat(value || 0) * 100);

// Meio-dia local: o dia não muda ao converter para UTC, então a parcela não troca de mês.
const toApiDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  date.setHours(12, 0, 0, 0);
  return date.toISOString();
};

const errorMessage = (err, fallback) => {
  const detail = err.response?.data?.detail;
  return typeof detail === 'string' ? detail : err.message || fallback;
};

const HELP_STEPS = [
  'Informe o número de parcelas e a data da 1ª parcela.',
  'Clique em “Gerar parcelas iguais”. O sistema divide o valor total e cria uma parcela por mês.',
  'Se as parcelas não forem iguais, altere o vencimento e o valor em cada linha. Para incluir ou tirar uma parcela, use “Adicionar parcela” ou a lixeira.',
  'Clique em “Salvar parcelas”. Nada é alterado antes disso.',
  'Quando pagar uma parcela, volte a esta tela, preencha “Pago em” e salve.',
];

const HELP_NOTES = [
  'Ativo já cadastrado: ele aparece aqui com uma única parcela, no valor total da compra. É só seguir os passos acima.',
  'Mês nos relatórios: cada parcela entra no mês do seu vencimento. “Pago em” apenas registra quando foi pago e não muda esse mês.',
  'Valor do ativo: o valor de aquisição é sempre a soma das parcelas. Se as parcelas incluírem juros, esse valor aumenta.',
  'Custos de preparação: não entram aqui. Continue usando “Adicionar Custo”.',
  'Conferência: cada parcela aparece em Gestão Financeira > Transações. Ela precisa ser verificada pelo financeiro, como qualquer lançamento, para entrar na evolução mensal.',
  'Banco e classificação: são copiados do lançamento original da compra. Para mudar em uma parcela, edite o lançamento em Transações.',
  'O que não pode ser alterado aqui: parcelas com a situação “Verificada” (somente superadmin). Também não é possível remover o lançamento original da compra nem parcelas com anexo.',
];

function InstallmentsHelp() {
  return (
    <Accordion defaultExpanded disableGutters variant="outlined" sx={{ mb: 2 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography fontWeight={600}>Como usar esta tela</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Use esta tela quando a compra do ativo for paga em mais de uma vez. Assim, o valor é lançado
          no financeiro mês a mês, em vez de cair inteiro em um único mês.
        </Typography>
        <Box component="ol" sx={{ m: 0, pl: 2.5, typography: 'body2' }}>
          {HELP_STEPS.map((step) => (
            <li key={step}>{step}</li>
          ))}
        </Box>
        <Typography variant="body2" fontWeight={600} sx={{ mt: 1.5, mb: 0.5 }}>
          Bom saber
        </Typography>
        <Box component="ul" sx={{ m: 0, pl: 2.5, typography: 'body2' }}>
          {HELP_NOTES.map((note) => (
            <li key={note}>{note}</li>
          ))}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}

function AssetInstallmentsDialog({ asset, onClose, onSaved }) {
  const { isSuperAdmin, hasPermission } = useAuth();
  const canCreate = hasPermission('finance.transactions.create');
  const canDelete = hasPermission('finance.transactions.delete');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [quantity, setQuantity] = useState('2');
  const [firstDueDate, setFirstDueDate] = useState(null);
  const [saving, runSave] = useSubmitLock();
  const nextKey = useRef(0);

  const assetId = asset?.id;
  const isLocked = (row) => row.is_verified && !isSuperAdmin();
  const newKey = () => {
    nextKey.current += 1;
    return nextKey.current;
  };

  useEffect(() => {
    if (!assetId) return undefined;
    let active = true;
    setLoading(true);
    setError('');
    setRows([]);
    financeService
      .getAssetInstallments(assetId)
      .then((items) => {
        if (!active) return;
        setRows(
          items.map((item) => ({
            ...item,
            key: newKey(),
            amount: String(item.amount),
            due_date: new Date(item.due_date),
            payment_date: item.payment_date ? new Date(item.payment_date) : null,
          }))
        );
        setQuantity(String(Math.max(items.length, 2)));
        setFirstDueDate(items[0] ? new Date(items[0].due_date) : new Date());
      })
      .catch((err) => active && setError(errorMessage(err, 'Erro ao carregar parcelas')))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [assetId]);

  const updateRow = (key, field, value) =>
    setRows((current) => current.map((row) => (row.key === key ? { ...row, [field]: value } : row)));

  const addRow = () =>
    setRows((current) => {
      const last = current[current.length - 1];
      return [
        ...current,
        {
          key: newKey(),
          id: null,
          amount: '',
          due_date: last ? addMonths(last.due_date, 1) : new Date(),
          payment_date: null,
          is_verified: false,
          is_acquisition: false,
        },
      ];
    });

  // Divide o saldo não verificado em parcelas mensais iguais; o arredondamento fica na última.
  const generateRows = () => {
    const count = parseInt(quantity, 10);
    const locked = rows.filter(isLocked);
    const open = rows
      .filter((row) => !isLocked(row))
      .sort((a, b) => Number(b.is_acquisition) - Number(a.is_acquisition));
    const openCount = count - locked.length;
    const cents = rows.reduce((sum, row) => sum + toCents(row.amount), 0)
      - locked.reduce((sum, row) => sum + toCents(row.amount), 0);

    if (!isValid(firstDueDate) || !count || openCount < 1 || cents < openCount) {
      setError('Informe a 1ª data e uma quantidade maior que o número de parcelas verificadas');
      return;
    }

    const base = Math.floor(cents / openCount);
    const generated = Array.from({ length: openCount }, (_, index) => ({
      key: newKey(),
      id: null,
      payment_date: null,
      is_verified: false,
      is_acquisition: false,
      ...open[index],
      amount: ((index === openCount - 1 ? cents - base * (openCount - 1) : base) / 100).toFixed(2),
      due_date: addMonths(firstDueDate, index),
    }));

    setError('');
    setRows([...locked, ...generated].sort((a, b) => a.due_date - b.due_date));
  };

  const acquisitionLocked = rows.some((row) => row.is_acquisition && isLocked(row));
  const totalCents = rows.reduce((sum, row) => sum + toCents(row.amount), 0);
  const currentCents = toCents(asset?.initial_value);
  const isValidSchedule =
    rows.length > 0 &&
    rows.every(
      (row) =>
        toCents(row.amount) > 0 &&
        isValid(row.due_date) &&
        (!row.payment_date || isValid(row.payment_date))
    );

  const handleSave = () =>
    runSave(async () => {
      setError('');
      try {
        await financeService.saveAssetInstallments(assetId, {
          installments: rows.map((row) => ({
            id: row.id,
            amount: (toCents(row.amount) / 100).toFixed(2),
            due_date: toApiDate(row.due_date),
            payment_date: toApiDate(row.payment_date),
          })),
        });
        onSaved();
      } catch (err) {
        setError(errorMessage(err, 'Erro ao salvar parcelas'));
      }
    });

  const dateFieldProps = { textField: { size: 'small', sx: { minWidth: 150 } } };

  return (
    <Dialog open={Boolean(asset)} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Parcelas da aquisição: {asset?.name}</DialogTitle>
      <DialogContent dividers>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
          <InstallmentsHelp />

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {acquisitionLocked && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  A compra deste ativo já foi verificada pelo financeiro. Para dividir o valor dela em parcelas, peça a um superadmin. Se você adicionar parcelas sem essa alteração, o valor do ativo aumenta.
                </Alert>
              )}

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }} alignItems={{ sm: 'center' }}>
                <TextField
                  size="small"
                  type="number"
                  label="Nº de parcelas"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  inputProps={{ min: 1, max: 120 }}
                  sx={{ minWidth: 140 }}
                />
                <DatePicker
                  label="1ª data"
                  value={firstDueDate}
                  onChange={setFirstDueDate}
                  slotProps={dateFieldProps}
                />
                <Button variant="outlined" onClick={generateRows} disabled={!canCreate || !canDelete}>
                  Gerar parcelas iguais
                </Button>
              </Stack>

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell>Vencimento</TableCell>
                      <TableCell>Valor (R$)</TableCell>
                      <TableCell>Pago em</TableCell>
                      <TableCell>Situação</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row, index) => (
                      <TableRow key={row.key}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>
                          <DatePicker
                            value={row.due_date}
                            disabled={isLocked(row)}
                            onChange={(value) => updateRow(row.key, 'due_date', value)}
                            slotProps={dateFieldProps}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            type="number"
                            value={row.amount}
                            disabled={isLocked(row)}
                            onChange={(e) => updateRow(row.key, 'amount', e.target.value)}
                            inputProps={{ min: 0.01, step: 0.01 }}
                            sx={{ minWidth: 120 }}
                          />
                        </TableCell>
                        <TableCell>
                          <DatePicker
                            value={row.payment_date}
                            disabled={isLocked(row)}
                            onChange={(value) => updateRow(row.key, 'payment_date', value)}
                            slotProps={{ ...dateFieldProps, field: { clearable: true } }}
                          />
                        </TableCell>
                        <TableCell>
                          {row.is_verified ? (
                            <Chip size="small" color="success" label="Verificada" />
                          ) : (
                            <Chip
                              size="small"
                              variant="outlined"
                              color={row.payment_date ? 'primary' : 'default'}
                              label={row.payment_date ? 'Paga' : 'Em aberto'}
                            />
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title={row.is_acquisition ? 'A transação de aquisição não pode ser removida' : 'Remover'}>
                            <span>
                              <IconButton
                                size="small"
                                aria-label="Remover parcela"
                                disabled={row.is_acquisition || isLocked(row) || (row.id && !canDelete)}
                                onClick={() => setRows((current) => current.filter((item) => item.key !== row.key))}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                useFlexGap
                spacing={1}
                sx={{ mt: 2 }}
              >
                <Button startIcon={<AddIcon />} onClick={addRow} disabled={!canCreate}>
                  Adicionar parcela
                </Button>
                <Typography fontWeight={600}>Total: {formatCurrency(totalCents / 100)}</Typography>
              </Stack>

              {totalCents !== currentCents && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  O valor de aquisição do ativo passará de {formatCurrency(currentCents / 100)} para {formatCurrency(totalCents / 100)}.
                </Alert>
              )}
            </>
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </LocalizationProvider>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || loading || !isValidSchedule}>
          Salvar parcelas
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AssetInstallmentsDialog;
